import { eq, pluralize } from '@art-suite/art-core-ts';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { PersistentCacheInterface, PromisedTypedCacheEntry, usePersistentCache } from '../PersistentCache';
import { type ConcurrencyManager, createConcurrencyManager } from './createConcurrencyManager';
import {
  DEFAULT_BACKOFF_FACTOR,
  DEFAULT_MAX_AGE_MS,
  DEFAULT_MAX_CONCURRENT_REQUESTS,
  DEFAULT_MAX_RETRY_DELAY_MS,
  DEFAULT_MAX_RETRY_MS,
  DEFAULT_PRIORITY,
  DEFAULT_RETRY_DELAY_MS,
  DEFAULT_STALE_AGE_MS,
} from './defaults';
import { requestWithRetry } from './requestWithRetry';

const useIsOfflineFalse = () => false;

/***
 * Options for the requestData function.
 * @param maxRetryMs - The maximum number of milliseconds to retry the request. If null, the request will be retried indefinitely.
 * @param retryDelayMs - The base delay between retries in milliseconds. This as the base, initial retry delay. Jitter is applied to every delay. 2nd, 3rd etc retries will multiply the base delay by an exponential backoff factor. See DEFAULT_RETRY_DELAY_MS
 * @param maxAgeMs - The maximum age of the data in milliseconds. Data older than this will be purged from the cache. See DEFAULT_MAX_AGE_MS
 * @param priority - The priority of the request. When the request concurrency limit is hit, higher priority items will get serviced first. Higher numbers indicate higher priority. Negative priorities are allowed. When priorities are equal, a newly queued request will go to the end of the list of requests with the same priority. The default priority is 0.
 * @param prefetchOnly - Whether to only prefetch the data and not store it in the cache.
 */
export interface RequestDataOptions {
  // request management options
  maxRetryDelayMs?: number;
  maxRetryMs?: number;
  priority?: number;
  retryBackoffFactor?: number;
  retryDelayMs?: number;
  retryServerFailures?: boolean;

  // cache management options
  maxAgeMs?: number;
  prefetchOnly?: boolean;
}

/**
 * Options for the refetchAll function. If no prefix and immediate is not true, the refetch will be debounced.
 * @param prefix - The prefix to filter the keys to refetch.
 * @param reason - The reason for the refetch.
 * @param immediate - Whether to refetch all immediately, without debouncing.
 */
export interface RefetchAllOptions {
  prefix?: string;
  reason?: string;
  immediate?: boolean;
}

export const QUERY_CACHE_NO_CHANGE = Symbol('RETURN_TO_SIGNAL_NO_CHANGE');

export type RequestDataFnOptions = {
  setProgress: (progress: number) => void;
  /**
   * Aborts when the originating subscriber goes away — hook unmount, key
   * change, or `enabled` flipping false. Long-running fetch functions (e.g.
   * polling an async backend job) should respect this signal and bail out
   * cleanly, throwing an aborted error.
   *
   * v1 limitation: when multiple hooks share the same key, requestData
   * dedupes onto a single in-flight promise. Only the FIRST subscriber's
   * signal is observed by the fetch function — later subscribers can be
   * left orphaned if the first one aborts before they unmount. Today this
   * doesn't bite (no real callers share keys for long-running fetches);
   * fixing it cleanly requires reference-counted abort, which we'll add
   * when a caller actually needs it.
   */
  signal: AbortSignal;
};

export type RequestDataFn<T> = (
  key: string,
  queryCache: QueryCache<unknown>,
  options: RequestDataFnOptions,
) => Promise<T | typeof QUERY_CACHE_NO_CHANGE>;

export interface QueryCache<T = unknown> {
  cache: PersistentCacheInterface<T>;
  /** Whether the cache is in offline mode. When offline, no new fetches will be made. */
  isOffline: boolean;
  /**
   * Always performs a fetch, but if there are multiple fetches requested for the same key (including hooks), only one fetch will be made for all of them.
   * @param key - The key to request the data from.
   * @param fetchFn - The function to fetch the data from.
   * @param options - The options to pass to the requestWithRetry.
   * @returns The PromisedTypedCacheEntry.
   */
  requestData: <T>(
    key: string,
    fetchFn: RequestDataFn<T>,
    options?: RequestDataOptions,
    requestDataFnOptions?: RequestDataFnOptions,
  ) => PromisedTypedCacheEntry<T>;

  /**
   * Get or fetch the value from the cache.
   * If the value is in the cache, return it.
   * If the value is not in the cache, uses requestData to fetch it and return it. If multiple fetches requested for the same key (including hooks), only one fetch will be made.
   * @param key - The key to get or fetch the value from.
   * @param fetchFn - The function to fetch the value from.
   * @param options - The options to pass to the fetch function.
   * @returns The value.
   */
  getOrFetch: <T>(key: string, fetchFn: RequestDataFn<T>, options?: RequestDataOptions) => Promise<T>;

  /**
   * Get or fetch the entry from the cache.
   * If the entry is in the cache, return it.
   * If the entry is not in the cache, uses requestData to fetch it and return it. If multiple fetches requested for the same key (including hooks), only one fetch will be made.
   * @param key - The key to get or fetch the entry from.
   * @param fetchFn - The function to fetch the entry from.
   * @param options - The options to pass to the fetch function.
   * @returns The CacheEntry.
   */
  getOrFetchEntry: <T>(key: string, fetchFn: RequestDataFn<T>, options?: RequestDataOptions) => PromisedTypedCacheEntry<T>;
  refetchAll: (options?: RefetchAllOptions) => unknown;
  _registerRefetch: (key: string, refetchFunction: () => Promise<void>) => () => void;
  _registerMetadataRefetch: (key: string, refetchFunction: () => Promise<void>) => () => void;

  concurrencyManager: ConcurrencyManager;

  defaultStaleAgeMs: number;
  defaultMaxRetryMs: number;
  defaultRetryDelayMs: number;
  defaultMaxRetryDelayMs: number;
  defaultMaxAgeMs: number;
  defaultPriority: number;
}

export type QueryCacheSubscriptionChangeHandler = (queryCache: QueryCache<unknown>, key: string, subscriberCount: number) => void;

const queryCacheContext = createContext<QueryCache | undefined>(undefined);

export interface QueryCacheProviderProps {
  children: ReactNode;
  maxConcurrentRequests?: number;
  /** Optional hook to determine if the app is offline. When offline, no new fetches will be made. */
  useIsOffline?: () => boolean;
  /** Debounce delay in milliseconds for refetchAll. First call executes immediately, subsequent calls within this window are debounced. Default: 60000 (1 minute). Set to 0 to disable debouncing. */
  refetchAllDebounceDelayMs?: number;
  verbose?: boolean;

  // default RequestDataOptions
  defaultStaleAgeMs?: number;
  defaultMaxRetryMs?: number;
  defaultRetryDelayMs?: number;
  defaultMaxRetryDelayMs?: number;
  defaultMaxAgeMs?: number;
  defaultPriority?: number;
  defaultBackoffFactor?: number;
  onSubscriptionChanges?: QueryCacheSubscriptionChangeHandler;
}

type ActiveFetchState = {
  attempt: number;
  cancelRetries: () => void;
};

type RefetchFunction = () => Promise<void>;

const DEFAULT_REFETCH_ALL_DEBOUNCE_DELAY_MS = 1000; // 1 second

export const QueryCacheProvider = ({
  children,
  maxConcurrentRequests = DEFAULT_MAX_CONCURRENT_REQUESTS,
  useIsOffline = useIsOfflineFalse,
  refetchAllDebounceDelayMs = DEFAULT_REFETCH_ALL_DEBOUNCE_DELAY_MS,
  verbose = false,
  defaultStaleAgeMs = DEFAULT_STALE_AGE_MS,
  defaultRetryDelayMs = DEFAULT_RETRY_DELAY_MS,
  defaultMaxRetryMs = DEFAULT_MAX_RETRY_MS,
  defaultMaxRetryDelayMs = DEFAULT_MAX_RETRY_DELAY_MS,
  defaultBackoffFactor = DEFAULT_BACKOFF_FACTOR,
  defaultMaxAgeMs = DEFAULT_MAX_AGE_MS,
  defaultPriority = DEFAULT_PRIORITY,
  onSubscriptionChanges,
}: QueryCacheProviderProps) => {
  const cache = usePersistentCache();
  const isOffline = useIsOffline();
  const isOfflineRef = useRef(isOffline);
  isOfflineRef.current = isOffline;
  verbose = true;

  const prevIsOfflineRef = useRef(isOffline);
  const concurrencyManager = useMemo(() => createConcurrencyManager(maxConcurrentRequests), [maxConcurrentRequests]);

  const onSubscriptionChangesRef = useRef<QueryCacheSubscriptionChangeHandler | undefined>(onSubscriptionChanges);
  onSubscriptionChangesRef.current = onSubscriptionChanges;
  const queryCacheRef = useRef<QueryCache<unknown> | null>(null);

  const activeFetchesRef = useRef<Map<string, ActiveFetchState>>(new Map());
  const activePromisesRef = useRef<Map<string, Promise<unknown>>>(new Map());

  const refetchSubscribersRef = useRef<Map<string, Set<RefetchFunction>>>(new Map());
  const metadataRefetchSubscribersRef = useRef<Map<string, Set<RefetchFunction>>>(new Map());

  const notifySubscriptionChanges = useCallback((key: string) => {
    if (!onSubscriptionChangesRef.current) return;
    const currentQueryCache = queryCacheRef.current;
    if (!currentQueryCache) return;
    const subscribers = refetchSubscribersRef.current.get(key);
    const subscriberCount = subscribers?.size ?? 0;
    onSubscriptionChangesRef.current(currentQueryCache, key, subscriberCount);
  }, []);

  const requestData = useCallback(
    <T,>(
      key: string,
      fetchFn: RequestDataFn<T>,
      options: RequestDataOptions = {},
      requestDataFnOptions: RequestDataFnOptions = { setProgress: () => {}, signal: new AbortController().signal },
    ): PromisedTypedCacheEntry<T> => {
      const {
        maxRetryMs = defaultMaxRetryMs,
        retryDelayMs = defaultRetryDelayMs,
        maxAgeMs = defaultMaxAgeMs,
        priority = defaultPriority,
        maxRetryDelayMs = defaultMaxRetryDelayMs,
        retryBackoffFactor = defaultBackoffFactor,
      } = options;

      const existingPromise = activePromisesRef.current.get(key) as PromisedTypedCacheEntry<T>;
      if (existingPromise) {
        return existingPromise;
      }

      // If offline, don't start new fetches - reject with error
      if (isOfflineRef.current) {
        if (verbose) console.log(`QueryCache: cannot fetch while offline for key: ${key}`);
        const error = new Error('Cannot fetch while offline');
        (error as any).isOffline = true;
        return Promise.reject(error) as PromisedTypedCacheEntry<T>;
      }

      // cancel any existing retries for this key
      if (activeFetchesRef.current.has(key)) {
        activeFetchesRef.current.get(key)?.cancelRetries();
        activeFetchesRef.current.delete(key);
      }

      const promise = requestWithRetry(
        concurrencyManager,
        ({ cancelRetries, attempt }) => {
          activeFetchesRef.current.set(key, { cancelRetries, attempt });
          // Bind subscriber-level abort to the retry loop: if the originating
          // hook unmounts mid-fetch, we cancel any pending retry attempts too.
          // The signal is also passed to fetchFn via requestDataFnOptions.
          if (!requestDataFnOptions.signal.aborted) {
            requestDataFnOptions.signal.addEventListener('abort', cancelRetries, { once: true });
          } else {
            cancelRetries();
          }
          if (!queryCacheRef.current) {
            throw new Error('QueryCache not found'); // should never happen
          }
          return fetchFn(key, queryCacheRef.current!, requestDataFnOptions);
        },
        {
          maxRetryMs,
          retryDelayMs,
          maxRetryDelayMs,
          retryBackoffFactor,
          priority,
          key,
          verbose,
          retryServerFailures: options.retryServerFailures, // since these are read-only requests, we can retry server failures
        },
      )
        .then(async result => {
          activeFetchesRef.current.delete(key);
          const now = Date.now();
          const expiresAt = maxAgeMs === Infinity ? Number.MAX_SAFE_INTEGER : now + maxAgeMs;
          if (result === QUERY_CACHE_NO_CHANGE) {
            await cache.freshen(key, expiresAt, now);
            return cache.getEntry(key, now, options.prefetchOnly);
          }
          const current = !options.prefetchOnly ? cache.getSync(key) : undefined;
          return options.prefetchOnly
            ? cache.setPrefetched(key, result, expiresAt, now, now)
            : cache.set(key, eq(current?.value, result) ? current?.value : result, expiresAt, now, now);
        })
        .catch(error => {
          const err = error instanceof Error ? error : new Error(String(error));
          throw err;
        })
        .finally(() => {
          activePromisesRef.current.delete(key);
        }) as PromisedTypedCacheEntry<T>;

      activePromisesRef.current.set(key, promise);
      return promise;
    },
    [
      cache,
      concurrencyManager,
      defaultBackoffFactor,
      defaultMaxAgeMs,
      defaultMaxRetryDelayMs,
      defaultMaxRetryMs,
      defaultPriority,
      defaultRetryDelayMs,
      verbose,
    ],
  );

  // cancel any remaining retries when the component unmounts
  useEffect(() => {
    const activeFetches = activeFetchesRef.current; // container never changes - only the values do
    return () => {
      for (const { cancelRetries } of activeFetches.values()) {
        cancelRetries();
      }
      activeFetches.clear();
    };
  }, []);

  const refetchAllInternal = useCallback(async (options?: RefetchAllOptions) => {
    const promises: Promise<void>[] = [];
    const allSubscribers = [...refetchSubscribersRef.current.entries(), ...metadataRefetchSubscribersRef.current.entries()];
    // if nothing to do
    if (allSubscribers.length === 0) return;
    if (options?.reason && verbose) {
      console.log(
        `QueryCache: refetching all ${pluralize(allSubscribers.length, 'active query', 'active queries')}: ${options.reason}`
          + (options?.prefix ? ` (prefix: ${options.prefix})` : ''),
      );
    }
    let count = 0;
    for (const [key, subscribers] of allSubscribers) {
      if (options?.prefix && !key.startsWith(options.prefix)) continue;
      for (const refetchFunction of subscribers) {
        count++;
        promises.push(refetchFunction());
      }
    }
    await Promise.all(promises);
    if (options?.reason && verbose) {
      console.log(
        `QueryCache: refetched ${pluralize(count, 'active query', 'active queries')}: ${options.reason}`
          + (options?.prefix ? ` (matching prefix: ${options.prefix})` : ''),
      );
    }
  }, [verbose, refetchSubscribersRef, metadataRefetchSubscribersRef]);

  const debouncedRefetchAllInternal = useDebouncedCallback(refetchAllInternal, refetchAllDebounceDelayMs, { leading: true, trailing: true });

  // only use debounce for full refetchAll, not for prefix-based refetchAll
  const refetchAll = useCallback(
    async (options?: RefetchAllOptions) => {
      if (options?.prefix || options?.immediate) return refetchAllInternal(options);
      else return debouncedRefetchAllInternal(options);
    },
    [refetchAllInternal, debouncedRefetchAllInternal],
  );

  // When going from offline to online, refresh all subscribed queries immediately.
  // Anti-flap is the responsibility of the upstream useIsOffline hook (e.g. a
  // heartbeat with backoff); debouncing here would swallow the legitimate signal.
  useEffect(() => {
    concurrencyManager.setActive(!isOffline);
    if (verbose) console.log(`QueryCache: set concurrency manager active to ${!isOffline}. Queue size: ${concurrencyManager.queueSize}`);
    if (prevIsOfflineRef.current && !isOffline) {
      refetchAll({ reason: 'went online', immediate: true });
    }
    prevIsOfflineRef.current = isOffline;
  }, [isOffline, refetchAll, verbose, concurrencyManager]);

  // When browser tab/window is re-focused, trigger a refetch of all subscribed queries.
  // Only runs in browser context (not React Native or Node.js test environments).
  useEffect(() => {
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchAll({ reason: 'browser tab/window was re-focused', immediate: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetchAll]);

  const queryCache = useMemo(
    () => {
      const getOrFetchEntry = async <T,>(key: string, fetchFn: RequestDataFn<T>, options: RequestDataOptions = {}): PromisedTypedCacheEntry<T> => {
        const cacheEntry = await cache.getEntry(key);
        if (cacheEntry) return cacheEntry as unknown as PromisedTypedCacheEntry<T>;
        return requestData(key, fetchFn, options);
      };
      const getOrFetch = async <T,>(key: string, fetchFn: RequestDataFn<T>, options: RequestDataOptions = {}): Promise<T> => {
        const entry = await getOrFetchEntry(key, fetchFn, options);
        if (!entry.value) {
          throw new Error('No value found for key: ' + key);
        }
        return entry.value;
      };
      return {
        // state
        cache,
        isOffline,

        // cache management options
        defaultStaleAgeMs,
        defaultMaxAgeMs,

        // request management options
        defaultMaxRetryMs,
        defaultRetryDelayMs,
        defaultMaxRetryDelayMs,
        defaultPriority,
        defaultBackoffFactor,

        // request functions
        requestData,
        getOrFetch,
        getOrFetchEntry,
        refetchAll,

        // internal, private functions
        _registerRefetch: (key: string, refetchFunction: () => Promise<void>) => {
          if (!refetchSubscribersRef.current.has(key)) {
            refetchSubscribersRef.current.set(key, new Set());
          }
          refetchSubscribersRef.current.get(key)!.add(refetchFunction);
          notifySubscriptionChanges(key);

          return () => {
            const subscribers = refetchSubscribersRef.current.get(key);
            if (subscribers) {
              subscribers.delete(refetchFunction);
              if (subscribers.size === 0) {
                refetchSubscribersRef.current.delete(key);
              }
            }
            notifySubscriptionChanges(key);
          };
        },
        _registerMetadataRefetch: (key: string, refetchFunction: () => Promise<void>) => {
          if (!metadataRefetchSubscribersRef.current.has(key)) {
            metadataRefetchSubscribersRef.current.set(key, new Set());
          }
          metadataRefetchSubscribersRef.current.get(key)!.add(refetchFunction);

          return () => {
            const subscribers = metadataRefetchSubscribersRef.current.get(key);
            if (subscribers) {
              subscribers.delete(refetchFunction);
              if (subscribers.size === 0) {
                metadataRefetchSubscribersRef.current.delete(key);
              }
            }
          };
        },
        concurrencyManager,
      };
    },
    [
      isOffline,
      cache,
      defaultBackoffFactor,
      defaultMaxAgeMs,
      defaultMaxRetryDelayMs,
      defaultMaxRetryMs,
      defaultPriority,
      defaultRetryDelayMs,
      defaultStaleAgeMs,
      notifySubscriptionChanges,
      refetchAll,
      concurrencyManager,
      requestData,
    ],
  );

  queryCacheRef.current = queryCache;

  return <queryCacheContext.Provider value={queryCache}>{children}</queryCacheContext.Provider>;
};

export const useQueryCache = <T = unknown>(): QueryCache<T> => {
  const context = useContext(queryCacheContext) as QueryCache<T>;
  if (!context) {
    throw new Error('useQueryCache must be used within a QueryCacheProvider');
  }
  return context;
};
